import React, { useContext, useState } from 'react';
import {
  CheckCircleOutlined,
  EditOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Descriptions, Space, Tag, Typography } from 'antd';

import UserStatus from '@totem/components/common/userStatus/UserStatus';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { isUserDeactivated } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import UserActivateModal from '@totem/components/users/container/actions/activate/UserActivateModal';
import UserRemoveModal from '@totem/components/users/container/actions/remove/UserRemoveModal';
import UserUpdateModal from '@totem/components/users/container/actions/update/UserUpdateModal';
import { getRoleString } from '@totem/components/users/container/utilities';
import SendWelcomeEmailButton from '@totem/components/users/details/SendWelcomeEmailButton';
import UserDetailsContext from '@totem/components/users/details/UserDetailsContext';

const { Text } = Typography;

const UserProfileCard = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { user, onRefresh } = useContext(UserDetailsContext);
  const [activeAction, setActiveAction] = useState<string>('');

  const isAdmin =
    permissions.organizationalAdministrator.hasPermissions(userProfile);
  const isDeactivated = user ? isUserDeactivated(user) : false;

  const handleActionClose = (doRefresh: boolean) => {
    setActiveAction('');
    if (doRefresh) {
      onRefresh();
    }
  };

  const userDisplayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const cardExtra = isAdmin && (
    <Space>
      <Button
        icon={<EditOutlined />}
        onClick={() => setActiveAction('editUser')}
      >
        Edit User
      </Button>
      <SendWelcomeEmailButton />
      {isDeactivated ? (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => setActiveAction('activateUser')}
        >
          Grant Access
        </Button>
      ) : (
        <Button
          danger
          icon={<StopOutlined />}
          onClick={() => setActiveAction('removeUser')}
        >
          Remove User
        </Button>
      )}
    </Space>
  );

  return (
    <>
      <IBCard
        isCovered
        title={
          <Space>
            <Avatar
              src={user.imageUrl}
              size={40}
              style={{ backgroundColor: '#1677ff' }}
            >
              {userDisplayName.charAt(0).toUpperCase()}
            </Avatar>
            <span>{userDisplayName}</span>
            {isDeactivated && <Tag color="red">Deactivated</Tag>}
          </Space>
        }
        extra={cardExtra}
      >
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
          <Descriptions.Item label="Role">
            {getRoleString(user.organization?.role)}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {user.status !== null && user.status !== undefined ? (
              <UserStatus status={user.status} />
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {user.lastLoginDate
              ? new Date(user.lastLoginDate).toLocaleDateString()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Member Since">
            {user.createdAt
              ? new Date(user.createdAt).toLocaleDateString()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {user.updatedAt
              ? new Date(user.updatedAt).toLocaleDateString()
              : '—'}
          </Descriptions.Item>
          {user.phoneNumber && (
            <Descriptions.Item label="Phone">
              {user.phoneNumber}
            </Descriptions.Item>
          )}
          {isDeactivated && user.organization?.deactivatedAt && (
            <Descriptions.Item label="Deactivated On">
              {new Date(user.organization.deactivatedAt).toLocaleDateString()}
            </Descriptions.Item>
          )}
          {isDeactivated && user.organization?.deactivatedFor && (
            <Descriptions.Item label="Reason for Removal" span={3}>
              {user.organization.deactivatedFor}
            </Descriptions.Item>
          )}
        </Descriptions>
      </IBCard>

      {activeAction === 'editUser' && (
        <UserUpdateModal
          user={user}
          open
          onClose={() => handleActionClose(true)}
        />
      )}
      {activeAction === 'removeUser' && (
        <UserRemoveModal
          user={user}
          open
          onClose={() => handleActionClose(true)}
        />
      )}
      {activeAction === 'activateUser' && (
        <UserActivateModal
          user={user}
          open
          onClose={() => handleActionClose(true)}
        />
      )}
    </>
  );
};

export default UserProfileCard;
